import React from "react";
import { useDispatch, useSelector } from "react-redux";
import commonActions from "../../Redux/common/actions";
import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import { IoMdCloseCircleOutline } from "react-icons/io";

const MS_VIEWER = "https://view.officeapps.live.com/op/embed.aspx?src=";

function detectFileType(url) {
  if (!url) return "unknown";
  const lower = url.toLowerCase();
  // Strip query-string before checking extension
  const path = lower.split("?")[0];
  if (path.endsWith(".pdf") || lower.includes("content-type=application%2Fpdf")) return "pdf";
  if (path.endsWith(".docx") || path.endsWith(".doc")) return "word";
  if (path.endsWith(".xlsx") || path.endsWith(".xls")) return "excel";
  if (path.endsWith(".pptx") || path.endsWith(".ppt")) return "powerpoint";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path)) return "image";
  return "unknown";
}

function FilePreviewContent({ fileUrl }) {
  const fileType = detectFileType(fileUrl);

  if (fileType === "image") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <img
          src={fileUrl}
          alt="File Preview"
          style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "8px" }}
        />
      </Box>
    );
  }

  if (fileType === "word" || fileType === "excel" || fileType === "powerpoint") {
    const officeSrc = `${MS_VIEWER}${encodeURIComponent(fileUrl)}`;
    return (
      <Box sx={{ width: "100%", height: "100%", overflow: "hidden", position: "relative" }}>
        <iframe
          src={officeSrc}
          title="File Preview"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            border: "none",
            borderRadius: "8px",
            width: "100%",
            height: "calc(100% + 40px)",
          }}
        />
      </Box>
    );
  }

  const src =
    fileType === "pdf" && !fileUrl.includes("#") ? `${fileUrl}#toolbar=0&navpanes=0` : fileUrl;

  return (
    <iframe
      src={src}
      title="File Preview"
      width="100%"
      height="100%"
      style={{ border: "none", borderRadius: "8px" }}
    />
  );
}

export default function FilePreviewModal() {
  const dispatch = useDispatch();
  const { showModal } = useSelector((state) => state.commonReducer);

  const isOpen = showModal?.show && showModal?.method === "FP";
  const fileUrl = isOpen ? showModal?.data : null;

  const handleClose = () => {
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: "FP", data: null },
    });
  };

  return (
    <Dialog
      open={!!isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      transitionDuration={0}
      keepMounted={false}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
          padding: "8px",
          backgroundColor: "rgba(240, 240, 251, 1)",
        },
      }}
    >
      <DialogContent
        sx={{
          width: "100%",
          backgroundColor: "rgba(240, 240, 251, 1)",
          p: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: { xs: "60vh", md: "80vh" },
            mt: 1,
            position: "relative",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              color: "#000",
              zIndex: 1,
              backgroundColor: "rgba(240, 240, 251, 1)",
              "&:hover": { backgroundColor: "rgba(240, 240, 251, 1)" },
            }}
          >
            <IoMdCloseCircleOutline />
          </IconButton>
          {fileUrl && <FilePreviewContent fileUrl={fileUrl} />}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
